import { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchFavoriteVideos, fetchLikedVideos } from '../../services/videoApi';
import type { Video } from '../../services/videoApi/videoApi.types';
import { UseMyFavoriteListReturn } from './types';
import { VideoData } from '../../components/VideoCard/types';
import { useIsMobile } from '../../config/breakpoints';
import { PAGINATION } from '../../config/constants';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { SnackbarType } from '../../components/Snackbar';

/** バックエンドから返される外部動画の追加フィールド */
interface VideoWithExternalFields extends Video {
  is_external?: boolean;
}

const PAGE_SIZE = 12;

/**
 * APIから返される動画データをVideoData型に変換するマッピング関数
 */
export const mapVideoToVideoData = (video: Video): VideoData => {
  const videoExt = video as VideoWithExternalFields;

  if (videoExt.is_external) {
    return {
      id: `ext_${video.id}`,
      external_id: video.id as string,
      isExternal: true,
      title: video.title || '無題',
      name: video.name || video.title || '無題',
      thumbnail: undefined,
      duration: video.duration,
      view_count: video.view_count,
      uploader: video.uploader ? {
        name: video.uploader.name,
        hospital: video.uploader.hospital ? { name: video.uploader.hospital.name } : undefined
      } : undefined,
      created_at: video.created_at,
      published_at: video.published_at,
      hospital_name: video.uploader?.hospital?.name,
      external_status: 'synced',
    };
  }

  return {
    id: video.id,
    title: video.title,
    name: video.name,
    thumbnail: video.thumbnail || video.thumbnail_url,
    duration: video.duration,
    view_count: video.view_count,
    // hasSpecialtyAssigned（videoSpecialtyStatus.ts）が「情報自体が渡っていない」（undefined）と
    // 「診療科が本当に未設定」（specialty: null）を区別できるよう、specialty はオブジェクトごと
    // （未設定なら null を明示して）引き継ぐ。specialty_name だけの詰め替えだとこの区別ができず、
    // 診療科設定済みの動画までケバブメニューの公開系が disabled になる事故が起きる（2026-09-21）。
    specialty: video.specialty ?? null,
    specialty_name: video.specialty?.name,
    uploader: video.uploader ? {
      id: video.uploader.id,
      name: video.uploader.name,
      icon_path: video.uploader.icon_path,
      hospital: video.uploader.hospital ? {
        name: video.uploader.hospital.name
      } : undefined
    } : undefined,
    uploader_id: video.uploader_id,
    created_at: video.created_at,
    published_at: video.published_at,
    visibility: video.visibility === 'public' || video.visibility === 'private' || video.visibility === 'hospital_only'
      ? video.visibility
      : undefined,
    images: video.images,
    // 「資料 N」バッジ用（外部動画（is_external）には資料機能が無いため上の早期returnでは引き継がない）
    materials: video.materials,
    conversion_status: video.conversion_status,
  };
};

/**
 * お気に入りとイイねした動画リストを管理するカスタムフック
 */
const useMyFavoriteList = (): UseMyFavoriteListReturn => {
  const showSnackbar = useSnackbar();
  const [showFavorites, setShowFavorites] = useState(true);
  const [showLiked, setShowLiked] = useState(true);
  const [rawVideos, setRawVideos] = useState<VideoData[]>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<string[]>([]);
  const [specialty, setSpecialty] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'DESC' | 'ASC'>('DESC');
  const [isLoading, setIsLoading] = useState(false);

  const isMobile = useIsMobile();

  const filteredSortedVideos = useMemo(() => {
    const filtered = specialty
      ? rawVideos.filter(v => v.specialty_name === specialty)
      : rawVideos;
    return [...filtered].sort((a, b) => {
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      return sortOrder === 'DESC' ? bDate - aDate : aDate - bDate;
    });
  }, [rawVideos, specialty, sortOrder]);

  const videos = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredSortedVideos.slice(start, start + PAGE_SIZE);
  }, [filteredSortedVideos, currentPage]);

  const pagination = useMemo(() => ({
    currentPage,
    totalPages: Math.ceil(filteredSortedVideos.length / PAGE_SIZE) || 1,
    totalItems: filteredSortedVideos.length
  }), [filteredSortedVideos, currentPage]);

  const loadVideos = useCallback(async (showFav: boolean, showLike: boolean) => {
    setIsLoading(true);
    try {
      const fetchOptions = {
        page: 1,
        limit: PAGINATION.FAVORITE_LIST_PAGE_SIZE,
        sort_by: 'created_at'
      };

      const [favoriteData, likedData] = await Promise.all([
        showFav ? fetchFavoriteVideos(fetchOptions) : Promise.resolve({ videos: [] }),
        showLike ? fetchLikedVideos(fetchOptions) : Promise.resolve({ videos: [] }),
      ]);

      const allVideos = [
        ...(favoriteData.videos || []).map(mapVideoToVideoData),
        ...(likedData.videos || []).map(mapVideoToVideoData),
      ];

      // 重複を除去（同じ動画がお気に入りといいねの両方にある場合）
      const uniqueVideos = allVideos.filter((video, index, self) =>
        index === self.findIndex(v => v.id === video.id)
      );

      const specialties = Array.from(new Set(
        uniqueVideos.map(v => v.specialty_name).filter((s): s is string => !!s)
      )).sort();

      setSpecialtyOptions(specialties);
      setRawVideos(uniqueVideos);
      setCurrentPage(1);
    } catch {
      showSnackbar('動画の取得に失敗しました', SnackbarType.ERROR);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVideos(showFavorites, showLiked);
  }, [loadVideos, showFavorites, showLiked]);

  const handleSortOrderChange = useCallback((order: 'DESC' | 'ASC') => {
    setSortOrder(order);
    setCurrentPage(1);
  }, []);

  const handleFavoriteToggle = useCallback(() => {
    setShowFavorites(prev => !prev);
  }, []);

  const handleLikedToggle = useCallback(() => {
    setShowLiked(prev => !prev);
  }, []);

  const handleSpecialtyChange = useCallback((sp: string) => {
    setSpecialty(sp);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  return {
    isMobile,
    videos,
    pagination,
    currentPage,
    sortOrder,
    showFavorites,
    showLiked,
    specialty,
    specialtyOptions,
    isLoading,
    handleSortOrderChange,
    handlePageChange,
    handleFavoriteToggle,
    handleLikedToggle,
    handleSpecialtyChange,
  };
};

export default useMyFavoriteList;
